
import { STORAGE_KEYS } from 'constants/index';

class StorageService {
  private storage: Storage;

  constructor(persistent: boolean = true) {
    this.storage = persistent ? window.localStorage : window.sessionStorage;
  }

  get<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Error getting item ${key} from storage:`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error setting item ${key} in storage:`, error);
    }
  }

  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error(`Error removing item ${key} from storage:`, error);
    }
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  has(key: string): boolean {
    return this.storage.getItem(key) !== null;
  }
}

export const appLocalStorage = new StorageService(true);
export const appSessionStorage = new StorageService(false);

export const tokenStorage = {
  setTokens: (accessToken: string, refreshToken: string) => {
    // Always persist tokens in localStorage to ensure cross-tab availability
    appLocalStorage.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    appLocalStorage.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    // Touch a well-known key to trigger the storage event across tabs
    try { localStorage.setItem('__auth_event__', `set:${Date.now()}`); } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:token-changed')); } catch {}
  },

  getAccessToken: (): string | null => {
    return appLocalStorage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getRefreshToken: (): string | null => {
    return appLocalStorage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  },

  clearTokens: () => {
    appLocalStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    appLocalStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    // Touch a well-known key to trigger the storage event across tabs
    try { localStorage.setItem('__auth_event__', `clear:${Date.now()}`); } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:token-changed')); } catch {}
  },

  hasValidToken: (): boolean => {
    return tokenStorage.getAccessToken() !== null;
  },
};

export default StorageService;

